/*
 * Black Friday Early Access (Disney) pages.
 * Shared by the landing, confirmation, waiting and password sections:
 * countdowns, the Klaviyo sign-up form, scroll-to links, the iCal download
 * and the "Geen code?" switch on the password page.
 */
(() => {
  if (window.bfDisneyReady) return;
  window.bfDisneyReady = true;

  const KLAVIYO_COMPANY_ID = 'RVj7fw';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const pad = (value) => String(value).padStart(2, '0');

  /* Countdown: hides for good at zero, so the layout closes up. */
  const initCountdown = (root) => {
    const target = Date.parse(root.dataset.bfCountdown);
    if (Number.isNaN(target)) return;

    const fields = {
      days: root.querySelector('[data-unit="days"]'),
      hours: root.querySelector('[data-unit="hours"]'),
      minutes: root.querySelector('[data-unit="minutes"]'),
      seconds: root.querySelector('[data-unit="seconds"]'),
    };

    let timer = null;
    const tick = () => {
      const seconds = Math.floor((target - Date.now()) / 1000);
      if (seconds <= 0) {
        root.hidden = true;
        if (timer) clearInterval(timer);
        return;
      }
      fields.days.textContent = pad(Math.floor(seconds / 86400));
      fields.hours.textContent = pad(Math.floor((seconds % 86400) / 3600));
      fields.minutes.textContent = pad(Math.floor((seconds % 3600) / 60));
      fields.seconds.textContent = pad(seconds % 60);
    };

    tick();
    if (!root.hidden) timer = setInterval(tick, 1000);
  };

  /* Klaviyo sign-up: e-mail only, posts to the list of the section. */
  const initSignup = (form) => {
    const input = form.querySelector('input[type="email"]');
    const error = form.querySelector('[data-bf-error]');
    const errorText = form.querySelector('[data-bf-error-text]');
    const success = form.querySelector('[data-bf-success]');
    const submit = form.querySelector('[type="submit"]');

    const showError = (message) => {
      form.classList.add('is-error');
      input.setAttribute('aria-invalid', 'true');
      errorText.textContent = message;
      error.hidden = false;
    };

    const clearError = () => {
      form.classList.remove('is-error');
      input.removeAttribute('aria-invalid');
      error.hidden = true;
    };

    input.addEventListener('input', () => {
      if (form.classList.contains('is-error')) clearError();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearError();

      const email = input.value.trim();
      if (!EMAIL_PATTERN.test(email)) {
        showError(form.dataset.errorEmail);
        return;
      }

      const listId = (form.dataset.listId || '').trim();
      if (!listId) {
        showError(form.dataset.errorGeneral);
        return;
      }

      form.classList.add('is-loading');
      submit.disabled = true;

      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: form.dataset.source || 'Black Friday Early Access',
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          if (form.dataset.redirect) {
            window.location.href = form.dataset.redirect;
            return;
          }
          form.classList.add('is-success');
          success.hidden = false;
          success.focus();
        })
        .catch(() => showError(form.dataset.errorGeneral))
        .finally(() => {
          form.classList.remove('is-loading');
          submit.disabled = false;
        });
    });
  };

  /* iCal: builds the .ics file in the browser. iOS saves a downloaded .ics to
     Files, so there the event opens directly and Safari offers "Add to Calendar". */
  const downloadIcs = (button) => {
    const escape = (text) => text.replace(/([,;\\])/g, '\\$1');
    const { start, end, title, details, url } = button.dataset;
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Achate//Black Friday Early Access//NL',
      'BEGIN:VEVENT',
      `UID:bf-early-access-${start}@achate`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${escape(title)}`,
      `DESCRIPTION:${escape(details)}`,
      `LOCATION:${escape(url)}`,
      `URL:${url}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIos) {
      window.location.href = `data:text/calendar;charset=utf8,${encodeURIComponent(ics)}`;
      return;
    }

    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    link.download = 'achate-black-friday-early-access.ics';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  document.addEventListener('click', (event) => {
    const scrollLink = event.target.closest('[data-bf-scroll-to]');
    if (scrollLink) {
      const target = document.querySelector(scrollLink.dataset.bfScrollTo);
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    const icsButton = event.target.closest('[data-bf-ics]');
    if (icsButton) {
      downloadIcs(icsButton);
      return;
    }

    /* Password page: swap the code form for the sign-up form and back. */
    const toggle = event.target.closest('[data-bf-toggle-panel]');
    if (toggle) {
      event.preventDefault();
      const card = toggle.closest('[data-bf-panels]');
      const next = card.querySelector(`[data-bf-panel="${toggle.dataset.bfTogglePanel}"]`);
      card.querySelectorAll('[data-bf-panel]').forEach((panel) => {
        panel.hidden = panel !== next;
      });
      const field = next.querySelector('input');
      if (field) field.focus();
    }
  });

  const once = (element, setup) => {
    if (element.dataset.bfReady) return;
    element.dataset.bfReady = 'true';
    setup(element);
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-bf-countdown]').forEach((element) => once(element, initCountdown));
    root.querySelectorAll('[data-bf-signup]').forEach((element) => once(element, initSignup));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders a section after each settings change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
